import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { BudgetLine } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchBudgets, fetchExpectedIncome, fetchSummary, type ExpectedIncomeResponse, type SummaryResponse } from "../services/expensesService";
import BudgetCharts, { ProgressBar, STATUS_TONES } from "../components/BudgetCharts";
import BudgetAutoAdjustCard from "../components/BudgetAutoAdjustCard";
import BudgetPeriodCard from "../components/BudgetPeriodCard";
import BudgetPeriodFilter from "../components/BudgetPeriodFilter";
import ExecutiveSummary, { DetailSection } from "../components/ExecutiveSummary";
import PayrollBreakdown from "../components/PayrollBreakdown";
import ProjectedFlowCard from "../components/ProjectedFlowCard";
import { ErrorBox, Kpi, Page, Spinner } from "../components/ui";
import { useBudgetPeriod } from "../hooks/useBudgetPeriod";
import { useExpensesOwner } from "../hooks/useExpensesOwner";
import { budgetInsights } from "../lib/budgetInsights";
import { budgetPeriodAnalysis, budgetPeriodFetchRange, budgetPeriodToQuery } from "../lib/budgetPeriod";
import { BUDGET_LINES_NEW_PARAM, BUDGET_LINES_PATH } from "../lib/budgetRoutes";
import { budgetTimeline, isCalendarYear } from "../lib/budgetTimeline";
import { CATEGORY_COLORS, categoryLabel, currentDay, currentMonth, money, monthLabel } from "../lib/format";
import { projectedFlow } from "../lib/projectedFlow";
import { ui } from "../theme";

// Presupuestos → Dashboard (#613, #1332). Un solo periodo «desde – hasta» de
// meses completos gobierna toda la página: el resumen ejecutivo, el plan contra
// lo real, el mes a mes con pronóstico, el flujo presupuestado y la ejecución
// por categoría se calculan con él. Las líneas se capturan en Desglose
// (BudgetLinesSection).

const keyOf = (range: { from: string; to: string }) => `${range.from}|${range.to}`;

export default function BudgetsSection() {
  const { t, i18n } = useTranslation(["common", "expensesStatements"]);
  const tx = (k: string, o?: Record<string, unknown>) => t(`expensesStatements:budgetExec.${k}`, o);
  const language = i18n.language;
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const navigate = useNavigate();
  const period = useBudgetPeriod();
  const { range } = period;
  const [lines, setLines] = useState<BudgetLine[]>([]);
  // Lo gastado y el ingreso esperado llevan el periodo para el que se pidieron:
  // nunca se pintan los datos de un periodo bajo la etiqueta de otro.
  const [loaded, setLoaded] = useState<{ key: string; summary: SummaryResponse } | null>(null);
  const [incomeFor, setIncomeFor] = useState<{ key: string; data: ExpectedIncomeResponse | null; error: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const owner = useExpensesOwner();
  const [payrollOpen, setPayrollOpen] = useState(false);

  // Un cambio de periodo vuelve a pedir todo; una respuesta que llega tarde no pisa a la nueva.
  const requestId = useRef(0);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    const key = keyOf(range);
    setLoading(true);
    setError(null);
    // El ingreso esperado es un apoyo: si falla, el resto de la página sigue.
    fetchExpectedIncome({ from: range.from, to: range.to })
      .then((r) => id === requestId.current && setIncomeFor({ key, data: r, error: null }))
      .catch((err) => id === requestId.current && setIncomeFor({ key, data: null, error: apiErrorMessage(err) || "load" }));
    try {
      // El periodo y, si incluye lo que viene, la historia que miran el ritmo y el pronóstico.
      const [b, s] = await Promise.all([fetchBudgets(), fetchSummary(budgetPeriodFetchRange(range, currentMonth()))]);
      if (id !== requestId.current) return;
      setLines(b);
      setLoaded({ key, summary: s });
    } catch (err) {
      if (id === requestId.current) setError(apiErrorMessage(err) || "load");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  const today = currentDay();
  const summary = loaded && loaded.key === keyOf(range) ? loaded.summary : null;
  const incomeNow = incomeFor && incomeFor.key === keyOf(range) ? incomeFor : null;
  const income = incomeNow?.data ?? null;
  const incomeError = incomeNow?.error ?? null;
  const incomeLoading = !incomeNow;
  const timeline = useMemo(() => (summary ? budgetTimeline(lines, summary.expenses, range, today) : null), [lines, summary, range, today]);
  const analysis = useMemo(() => (summary ? budgetPeriodAnalysis(lines, summary.expenses, range, today) : null), [lines, summary, range, today]);
  const flow = useMemo(() => (income && timeline ? projectedFlow(income.months, timeline.months) : null), [income, timeline]);
  const insights = useMemo(() => (timeline ? budgetInsights({ timeline, analysis, flow }) : null), [timeline, analysis, flow]);

  const scope = isCalendarYear(range.from, range.to) ? range.from.slice(0, 4) : range.from === range.to ? monthLabel(range.from, language) : `${monthLabel(range.from, language)} – ${monthLabel(range.to, language)}`;
  const fmt = (v: number | null | undefined) => money(v, "MXN", language);
  const errorText = error === "load" ? t("expenses.errors.load") : error;
  // El periodo viaja a Desglose para que abra con el mismo filtro.
  const linesPath = `${BUDGET_LINES_PATH}${budgetPeriodToQuery(range, currentMonth())}`;
  const newLinePath = `${BUDGET_LINES_PATH}${budgetPeriodToQuery(range, currentMonth(), `?${BUDGET_LINES_NEW_PARAM}=1`)}`;

  const tl = timeline;
  // Desglose de nómina (#1424): solo dueños, y solo de los meses que ya empezaron
  // (lo que viene todavía no tiene nada «gastado» que explicar).
  const payrollTo = range.to > currentMonth() ? currentMonth() : range.to;
  const canSeePayroll = owner === true && range.from <= payrollTo;
  const available = tl ? tl.totalBudget - tl.spentToDate : 0;
  const executed = tl && tl.totalBudget > 0 ? (tl.spentToDate / tl.totalBudget) * 100 : null;

  return (
    <Page
      title={t("expenses.menu.budgets")}
      subtitle={t("expenses.budgets.subtitle")}
      actions={
        <>
          <button type="button" className={c.secondary} onClick={() => navigate(linesPath)}>
            <i className="ri-list-check-2 mr-1" />
            {t("expenses.budgets.goToLines")}
          </button>
          {/* El formulario vive en Desglose: `?new=1` lo deja a la vista y con el foco. */}
          <button type="button" className={c.primary} onClick={() => navigate(newLinePath)}>
            <i className="ri-add-line mr-1" />
            {t("expenses.budgets.newLine")}
          </button>
        </>
      }
    >
      <BudgetPeriodFilter period={period} />

      <ErrorBox message={errorText} />
      {!tl && !error && <Spinner />}

      {tl && insights && (
        <div className={`space-y-4 ${loading ? "opacity-60" : ""}`} data-testid="budget-dashboard">
          <ExecutiveSummary title={tx("title")} scope={scope} verdict={insights.verdict} findings={insights.findings} actions={insights.actions}>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="exec-kpis">
              <Kpi
                label={tx("kpi.budget")}
                value={fmt(tl.totalBudget)}
                hint={tl.planMonths > 0 && tl.planMonths < tl.months.length ? tx("kpi.budgetHintPartial", { planned: tl.planMonths, count: tl.months.length }) : tx("kpi.budgetHint", { count: tl.months.length })}
              />
              <Kpi label={tx("kpi.spent")} value={insights.window === "future" ? "—" : fmt(tl.spentToDate)} hint={executed == null ? tx("kpi.spentNoBudget") : tx("kpi.spentHint", { pct: executed.toFixed(0) })} tone={executed != null && executed > 100 ? "bad" : undefined} />
              <Kpi
                label={tx(insights.window === "past" ? (available < 0 ? "kpi.over" : "kpi.left") : "kpi.projected")}
                value={insights.window === "past" ? fmt(Math.abs(available)) : fmt(tl.projectedYearEnd)}
                hint={tx(insights.window === "past" ? "kpi.closedHint" : "kpi.projectedHint", { rate: fmt(tl.runRate) })}
                tone={tl.totalBudget > 0 ? ((insights.window === "past" ? available < 0 : tl.projectedYearEnd > tl.totalBudget) ? "bad" : "good") : undefined}
              />
              <Kpi
                label={tx("kpi.flow")}
                value={flow && flow.remaining.months > 0 ? fmt(flow.remaining.net) : "—"}
                hint={flow && flow.remaining.months > 0 ? tx("kpi.flowHint", { income: fmt(flow.remaining.income), spend: fmt(flow.remaining.spend) }) : tx(incomeLoading ? "kpi.flowLoading" : incomeError ? "kpi.flowError" : "kpi.flowNone")}
                tone={flow && flow.remaining.months > 0 ? (flow.remaining.net < 0 ? "bad" : "good") : undefined}
              />
            </div>
          </ExecutiveSummary>

          <DetailSection id="categories" title={tx("sections.categories.title")} takeaway={tx("sections.categories.takeaway")} defaultOpen>
            <div className={c.card} data-testid="budget-execution">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead>
                    <tr className={`text-left text-xs ${c.muted}`}>
                      <th className="px-2 py-2">{t("expenses.fields.category")}</th>
                      <th className="px-2 py-2 text-right">{t("expenses.budgets.budgeted")}</th>
                      <th className="px-2 py-2 text-right">{t("expenses.budgets.spent")}</th>
                      <th className="px-2 py-2 text-right">{t("expenses.budgets.difference")}</th>
                      <th className="px-2 py-2 text-right">{tx("col.projected")}</th>
                      <th className="w-1/5 px-2 py-2">{t("expenses.budgets.execution")}</th>
                      <th className="px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {tl.categories.map((r) => {
                      const diff = r.budget - r.spent;
                      // La nómina se despliega persona por persona (#1424). Lleva sueldos: solo dueños.
                      const expandable = r.category === "nomina" && canSeePayroll;
                      const expanded = expandable && payrollOpen;
                      return (
                        <Fragment key={r.category}>
                        <tr className={`border-t ${c.border} ${c.text}`} data-testid={`exec-row-${r.category}`}>
                          <td className="px-2 py-2.5">
                            {expandable ? (
                              <button
                                type="button"
                                aria-expanded={expanded}
                                aria-controls={expanded ? "payroll-breakdown-row" : undefined}
                                title={tx("payroll.toggle")}
                                data-testid="payroll-toggle"
                                onClick={() => setPayrollOpen((o) => !o)}
                                className="inline-flex items-center gap-2 text-left hover:text-teal-600"
                              >
                                <i className={`${expanded ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} -ml-1 text-base ${c.muted}`} />
                                <span className="h-2.5 w-2.5 rounded-full" style={{ background: CATEGORY_COLORS[r.category] }} />
                                {categoryLabel(r.category, language)}
                                <span className="text-xs text-teal-600">{tx(expanded ? "payroll.hide" : "payroll.show")}</span>
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-2">
                                <span className="h-2.5 w-2.5 rounded-full" style={{ background: CATEGORY_COLORS[r.category] }} />
                                {categoryLabel(r.category, language)}
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-2.5 text-right tabular-nums">{r.budget ? fmt(r.budget) : "—"}</td>
                          <td className="px-2 py-2.5 text-right tabular-nums">{fmt(r.spent)}</td>
                          <td className={`px-2 py-2.5 text-right tabular-nums ${diff < 0 ? "text-red-500" : ""}`}>{r.budget ? fmt(diff) : "—"}</td>
                          <td className={`px-2 py-2.5 text-right tabular-nums ${r.budget > 0 && r.projected > r.budget ? "text-red-500" : c.muted}`}>{insights.window === "past" ? "—" : fmt(r.projected)}</td>
                          <td className="px-2 py-2.5">
                            <div className="flex items-center gap-2">
                              <ProgressBar pct={r.pct} projectedPct={r.budget > 0 && insights.window !== "past" ? (r.projected / r.budget) * 100 : null} status={r.status} label={categoryLabel(r.category, language)} />
                              <span className={`w-12 shrink-0 text-right text-xs tabular-nums ${r.status === "over" ? "font-semibold text-red-500" : c.muted}`}>{r.pct == null ? "—" : `${r.pct.toFixed(0)}%`}</span>
                            </div>
                          </td>
                          <td className="px-2 py-2.5 text-right">
                            <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_TONES[r.status].chip}`}>{t(`expenses.budgets.charts.status.${r.status}`)}</span>
                          </td>
                        </tr>
                        {expanded && (
                          <tr id="payroll-breakdown-row" className={c.surface}>
                            <td colSpan={7} className="px-4 py-3">
                              <PayrollBreakdown from={range.from} to={payrollTo} rowSpent={r.spent} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      );
                    })}
                    {tl.categories.length === 0 && (
                      <tr>
                        <td colSpan={7} className={`px-4 py-6 text-center ${c.muted}`}>
                          {t("expenses.budgets.empty")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {tl.categories.length > 0 && (
                    // El total de la tabla es el mismo de los indicadores de arriba.
                    <tfoot>
                      <tr className={`border-t-2 font-semibold ${c.border} ${c.text}`} data-testid="exec-row-total">
                        <td className="px-2 py-2.5">{tx("col.total")}</td>
                        <td className="px-2 py-2.5 text-right tabular-nums">{tl.totalBudget ? fmt(tl.totalBudget) : "—"}</td>
                        <td className="px-2 py-2.5 text-right tabular-nums">{fmt(tl.spentToDate)}</td>
                        <td className={`px-2 py-2.5 text-right tabular-nums ${available < 0 ? "text-red-500" : ""}`}>{tl.totalBudget ? fmt(available) : "—"}</td>
                        <td className={`px-2 py-2.5 text-right tabular-nums ${tl.totalBudget > 0 && tl.projectedYearEnd > tl.totalBudget ? "text-red-500" : ""}`}>{insights.window === "past" ? "—" : fmt(tl.projectedYearEnd)}</td>
                        <td className="px-2 py-2.5 text-right text-xs tabular-nums" colSpan={2}>
                          {executed == null ? "—" : `${executed.toFixed(0)}%`}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              <p className={`mt-2 text-xs ${c.muted}`}>{tx("sections.categories.note")}</p>
              {tl.spentYear - tl.spentToDate > 0.005 && (
                <p className={`mt-1 text-xs ${c.muted}`} data-testid="outside-plan">
                  <i className="ri-information-line mr-1" />
                  {tx("sections.categories.outside", { amount: fmt(tl.spentYear - tl.spentToDate) })}
                </p>
              )}
            </div>
          </DetailSection>

          <DetailSection id="charts" title={tx("sections.charts.title")} takeaway={tx("sections.charts.takeaway")} defaultOpen>
            <BudgetCharts timeline={tl} onPickMonth={(p) => period.apply({ from: p, to: p })} />
          </DetailSection>

          {analysis && (
            <DetailSection id="months" title={tx("sections.months.title")} takeaway={tx("sections.months.takeaway")}>
              <BudgetPeriodCard analysis={analysis} />
            </DetailSection>
          )}

          <DetailSection id="flow" title={tx("sections.flow.title")} takeaway={tx("sections.flow.takeaway")}>
            <ProjectedFlowCard timeline={tl} data={income} error={incomeError} />
          </DetailSection>

          <DetailSection id="adjust" title={tx("sections.adjust.title")} takeaway={tx("sections.adjust.takeaway")}>
            <BudgetAutoAdjustCard refreshKey={lines} onApplied={() => void load()} />
          </DetailSection>
        </div>
      )}
    </Page>
  );
}
